import React, { useState } from "react";
import {
    DataProviderContext,
    List,
    TopToolbar,
    useListContext,
} from "react-admin";
import UpdraftDataProvider from "../UpdraftDataProvider";
import Button from "@mui/material/Button";
import DescriptionIcon from "@mui/icons-material/Description";
import DownloadIcon from "@mui/icons-material/Download";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import IconButton from "@mui/material/IconButton";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import Big from "big.js";

const toBig = (value: string | number | null | undefined): Big => {
    if (value === null || value === undefined) return new Big(0);
    return new Big(value);
};

const formatMoney = (value: Big | number | string | null | undefined): string => {
    if (value === null || value === undefined) return "";
    if (value instanceof Big) {
        return value.toNumber().toLocaleString("en-GB", { style: "currency", currency: "GBP" });
    }
    const num = typeof value === "string" ? parseFloat(value) : value;
    return num.toLocaleString("en-GB", { style: "currency", currency: "GBP" });
};

const formatDate = (dateStr: string): string => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-GB");
};

const ENTRY_TYPE_LABELS: Record<string, string> = {
    DISBURSMENT: "Disbursment",
    PLANNED_PAYMENT: "Planned payment",
    INTEREST_CHARGED: "Interest charged",
    REPAYMENT: "Repayment",
    INTEREST_WAIVER: "Interest waived",
    PRINCIPAL_WRITEOFF: "Principal write off",
    REFUND: "Refund",
    CLOSING_WRITEOFF: "Closing write off",
    CLOSING_REFUND: "Closing Refund",
};

const getEntryTypeLabel = (entryType: string, totalAmount: Big, interestDelta: Big): string => {
    if (entryType === "REPAYMENT" && totalAmount.gt(0)) {
        return "Repayment returned";
    }
    if (entryType === "INTEREST_CHARGED" && interestDelta.lt(0)) {
        return "Interest paid";
    }
    return ENTRY_TYPE_LABELS[entryType] || entryType;
};

const exportToCsv = (data: any[], externalId: string | number) => {
    const headers = [
        "Date",
        "Entry Type",
        "Opening P+I Balance",
        "Amount",
        "Closing P+I Balance",
        "Principal Opening",
        "Principal Delta",
        "Principal Closing",
        "Interest Opening",
        "Interest Delta",
        "Interest Closing",
    ];

    const rows = data.map((record: any) => {
        const d = record.schedule_entity_data;

        const openingPrincipal = toBig(d?.opening_balance);
        const closingPrincipal = toBig(d?.closing_balance);
        const principalDelta = toBig(d?.principal_signed_delta);

        const closingInterest = toBig(d?.unpaid_interest);
        const interestDelta = toBig(d?.interest_signed_delta);
        const openingInterest = closingInterest.minus(interestDelta);

        const totalAmount = toBig(d?.total_signed_delta);

        return [
            formatDate(d?.date),
            getEntryTypeLabel(d?.entry_type, totalAmount, interestDelta),
            d?.opening_balance_with_interest ?? "",
            totalAmount.toString(),
            d?.closing_balance_with_interest ?? "",
            openingPrincipal.toString(),
            principalDelta.toString(),
            closingPrincipal.toString(),
            openingInterest.toString(),
            interestDelta.toString(),
            closingInterest.toString(),
        ];
    });

    const csvContent = [
        headers.join(","),
        ...rows.map(row => row.map(cell => `"${cell}"`).join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `actual_schedule_v2_${externalId}.csv`);
    link.click();
    URL.revokeObjectURL(url);
};

const ExportButton = ({ externalId }: { externalId: string | number }) => {
    const { data } = useListContext();

    const handleExport = () => {
        if (data) {
            exportToCsv(data, externalId);
        }
    };

    return (
        <Button
            onClick={handleExport}
            startIcon={<DownloadIcon />}
            size="small"
            disabled={!data || data.length === 0}
        >
            Export
        </Button>
    );
};

const ScheduleActions = ({ externalId }: { externalId: string | number }) => {
    const dataProvider = React.useContext(DataProviderContext) as UpdraftDataProvider;
    const vaultUrl = dataProvider?.getServiceUrl?.("vault") || "";
    const statementUrl = `${vaultUrl}/loanaccount/statement_v2/${externalId}/`;

    const handleViewStatement = async () => {
        const token = localStorage.getItem("token");
        try {
            const response = await fetch(statementUrl, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            const html = await response.text();
            const newWindow = window.open("", "_blank");
            if (newWindow) {
                newWindow.document.write(html);
                newWindow.document.close();
                newWindow.document.title = `Statement V2 - ${externalId}`;
            }
        } catch (error) {
            console.error("Fetch error:", error);
        }
    };

    return (
        <TopToolbar>
            <ExportButton externalId={externalId} />
            <Button
                onClick={handleViewStatement}
                startIcon={<DescriptionIcon />}
                size="small"
            >
                View Statement V2
            </Button>
        </TopToolbar>
    );
};

const ScheduleTable = () => {
    const { data } = useListContext();
    const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

    const toggleRow = (id: string) => {
        setExpandedRows(prev => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    };

    if (!data) return null;

    return (
        <TableContainer sx={{ maxHeight: "calc(100vh - 250px)" }}>
        <Table size="small" stickyHeader>
            <TableHead>
                <TableRow>
                    <TableCell sx={{ width: 48 }}></TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Entry type</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell align="right">P+I Balance</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {data.map((record: any) => {
                    const d = record.schedule_entity_data;
                    const id = record.id;
                    const isExpanded = expandedRows.has(id);

                    const openingPrincipal = toBig(d?.opening_balance);
                    const closingPrincipal = toBig(d?.closing_balance);
                    const principalDelta = toBig(d?.principal_signed_delta);

                    const closingInterest = toBig(d?.unpaid_interest);
                    const interestDelta = toBig(d?.interest_signed_delta);
                    const openingInterest = closingInterest.minus(interestDelta);

                    const totalAmount = toBig(d?.total_signed_delta);
                    const isInterestEntry = d?.entry_type === "INTEREST_CHARGED" || d?.entry_type === "INTEREST_WAIVER";

                    return (
                        <React.Fragment key={id}>
                            <TableRow
                                hover
                                onClick={() => toggleRow(id)}
                                sx={{
                                    cursor: "pointer",
                                    backgroundColor: data.indexOf(record) % 2 === 0 ? "#fff" : "#f9f9f9",
                                    "&:hover": {
                                        backgroundColor: "#e3f2fd !important",
                                    },
                                    ...(isInterestEntry && {
                                        color: "#777",
                                        "& td": {
                                            color: "#777",
                                            fontSize: "0.9em",
                                            padding: "4px 16px",
                                        },
                                    }),
                                }}
                            >
                                <TableCell>
                                    <IconButton size="small">
                                        {isExpanded ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
                                    </IconButton>
                                </TableCell>
                                <TableCell>{formatDate(d?.date)}</TableCell>
                                <TableCell>{getEntryTypeLabel(d?.entry_type, totalAmount, interestDelta)}</TableCell>
                                <TableCell align="right">{formatMoney(totalAmount)}</TableCell>
                                <TableCell align="right">{formatMoney(d?.closing_balance_with_interest)}</TableCell>
                            </TableRow>
                            {isExpanded && (
                                <>
                                    <TableRow style={{ backgroundColor: "#f5f5f5" }}>
                                        <TableCell></TableCell>
                                        <TableCell></TableCell>
                                        <TableCell style={{ color: "#666", paddingLeft: 16 }}>↳ Principal</TableCell>
                                        <TableCell align="right" style={{ color: "#666" }}>{formatMoney(principalDelta)}</TableCell>
                                        <TableCell align="right" style={{ color: "#666" }}>{formatMoney(closingPrincipal)}</TableCell>
                                    </TableRow>
                                    <TableRow style={{ backgroundColor: "#f5f5f5" }}>
                                        <TableCell></TableCell>
                                        <TableCell></TableCell>
                                        <TableCell style={{ color: "#666", paddingLeft: 16 }}>↳ Interest</TableCell>
                                        <TableCell align="right" style={{ color: "#666" }}>{formatMoney(interestDelta)}</TableCell>
                                        <TableCell align="right" style={{ color: "#666" }}>{formatMoney(closingInterest)}</TableCell>
                                    </TableRow>
                                </>
                            )}
                        </React.Fragment>
                    );
                })}
            </TableBody>
        </Table>
        </TableContainer>
    );
};

const ScheduleEntries = ({ externalId }: { externalId: string | number }) => {
    return (
        <List
            pagination={false}
            perPage={500}
            actions={<ScheduleActions externalId={externalId} />}
        >
            <ScheduleTable />
        </List>
    );
};

const ScheduleV2 = { list: ScheduleEntries };

export default ScheduleV2;